import React from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { ChevronUp, ChevronDown, Trash2, Plus } from "lucide-react";
import { IconPicker } from "./IconPicker";
import { inputCls } from "./AdminField";
import { slugify } from "../lib/slug";

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "blockquote"],
    ["clean"],
  ],
};

// Inline FAQ editor (question + answer, reorderable, hides on public page
// when empty). Rendered inside each expanded treatment-area accordion.
const FAQEditor = ({ faqs, onChange, testIdPrefix }) => {
  const list = Array.isArray(faqs) ? faqs : [];
  const set = (i, patch) => onChange(list.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = list.slice();
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const add = () => onChange([...list, { question: "", answer: "" }]);

  return (
    <div>
      <div className="space-y-3">
        {list.map((faq, i) => (
          <div key={i} data-testid={`${testIdPrefix}-faq-${i}`} className="rounded-lg border border-cream-2 p-3 bg-cream/40">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-ink-muted">FAQ #{i + 1}</span>
              <div className="flex items-center gap-1">
                <button type="button" data-testid={`${testIdPrefix}-faq-up-${i}`} onClick={() => move(i, -1)} disabled={i === 0} title="Move up" className="p-1.5 rounded-md text-ink-soft hover:bg-cream-2 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button type="button" data-testid={`${testIdPrefix}-faq-down-${i}`} onClick={() => move(i, 1)} disabled={i === list.length - 1} title="Move down" className="p-1.5 rounded-md text-ink-soft hover:bg-cream-2 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                  <ChevronDown className="h-4 w-4" />
                </button>
                <button type="button" data-testid={`${testIdPrefix}-faq-remove-${i}`} onClick={() => remove(i)} title="Delete" className="p-1.5 rounded-md text-red-600 hover:bg-red-50 transition-colors">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <input
                data-testid={`${testIdPrefix}-faq-question-${i}`}
                placeholder="Question"
                value={faq.question ?? ""}
                onChange={(e) => set(i, { question: e.target.value })}
                className={inputCls}
              />
              <textarea
                data-testid={`${testIdPrefix}-faq-answer-${i}`}
                placeholder="Answer"
                rows={3}
                value={faq.answer ?? ""}
                onChange={(e) => set(i, { answer: e.target.value })}
                className={inputCls}
              />
            </div>
          </div>
        ))}
      </div>
      <button type="button" data-testid={`${testIdPrefix}-faq-add`} onClick={add} className="mt-3 inline-flex items-center gap-2 rounded-full border border-sage/40 text-sage-dark px-4 py-2 text-sm font-semibold hover:bg-sage/10 transition-colors">
        <Plus className="h-4 w-4" /> Add FAQ
      </button>
    </div>
  );
};

// One accordion row's expanded body: every field for a treatment area.
const AreaFields = ({ area, patch, index }) => {
  const set = (fragment) => patch({ ...fragment });
  const setNested = (key, value) => patch({ [key]: { ...(area[key] || {}), ...value } });
  const previewSlug = area.slug || slugify(area.name || "");
  const testIdPrefix = `area-${index}`;

  return (
    <div className="space-y-6 border-t border-cream-2 px-5 py-6 bg-cream/30">
      {/* Tile & URL */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-ink mb-2">Name</label>
          <input data-testid={`${testIdPrefix}-name`} value={area.name || ""} onChange={(e) => set({ name: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className="block text-sm font-medium text-ink mb-2">URL slug</label>
          <div className="flex items-center gap-2">
            <span className="text-xs text-ink-muted whitespace-nowrap">/treatment-areas/</span>
            <input data-testid={`${testIdPrefix}-slug`} value={area.slug || ""} onChange={(e) => set({ slug: slugify(e.target.value) })} placeholder={previewSlug} className={inputCls} />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-ink mb-2">Homepage tile blurb (shown on hover)</label>
        <input data-testid={`${testIdPrefix}-blurb`} value={area.blurb || ""} onChange={(e) => set({ blurb: e.target.value })} className={inputCls} />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-ink mb-2">Icon</label>
          <IconPicker value={area.icon} onChange={(v) => set({ icon: v })} testId={`${testIdPrefix}-icon`} />
        </div>
        <div className="flex items-end justify-between gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-ink mb-2">Active</label>
            <p className="text-xs text-ink-muted">When off, the tile and page are hidden from visitors.</p>
          </div>
          <button
            type="button"
            data-testid={`${testIdPrefix}-active`}
            aria-pressed={area.active !== false}
            onClick={() => set({ active: area.active === false ? true : false })}
            className={`inline-flex h-6 w-11 items-center rounded-full transition-colors ${area.active !== false ? "bg-sage" : "bg-cream-2"}`}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${area.active !== false ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="pt-2">
        <h4 className="font-serif text-lg text-ink mb-1">Content</h4>
        <p className="text-xs text-ink-muted mb-3">The main copy shown on this treatment area&apos;s page.</p>
        <div className="bg-white rounded-lg border border-cream-2" data-testid={`${testIdPrefix}-body`}>
          <ReactQuill theme="snow" value={area.body_html || ""} onChange={(v) => set({ body_html: v })} modules={QUILL_MODULES} />
        </div>
      </div>

      {/* FAQs */}
      <div className="pt-2">
        <h4 className="font-serif text-lg text-ink mb-1">FAQs</h4>
        <p className="text-xs text-ink-muted mb-3">Add common questions and warm, honest answers. Section is hidden on the public page when empty. Adding at least 3 unlocks Google&apos;s FAQ rich snippets.</p>
        <FAQEditor faqs={area.faqs} onChange={(f) => set({ faqs: f })} testIdPrefix={testIdPrefix} />
      </div>

      {/* Per-page SEO */}
      <div className="pt-2">
        <h4 className="font-serif text-lg text-ink mb-1">Search-engine overrides</h4>
        <p className="text-xs text-ink-muted mb-3">Optional. Leave blank to fall back to the area name and the first paragraph of the content.</p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-ink mb-2">SEO title</label>
            <input data-testid={`${testIdPrefix}-seo-title`} value={area?.seo?.title || ""} onChange={(e) => setNested("seo", { title: e.target.value })} placeholder={`${area.name || "Area"} Counselling in Delta, BC`} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-2">Meta description</label>
            <textarea data-testid={`${testIdPrefix}-seo-description`} rows={2} value={area?.seo?.description || ""} onChange={(e) => setNested("seo", { description: e.target.value })} placeholder="One or two sentences shown under the link in Google results." className={inputCls} />
          </div>
        </div>
      </div>
    </div>
  );
};

// The main field renderer used by ContentEditor for `type: "treatmentareas"`.
// Renders each area as an accordion row: name + icon in the header, everything
// else inside the expandable body. Newly-added rows open expanded.
export const TreatmentAreasField = ({ field, draft, update }) => {
  const list = Array.isArray(draft?.treatment?.areas) ? draft.treatment.areas : [];
  const [openIdx, setOpenIdx] = React.useState(null);

  const setList = (next) => update("treatment.areas", next);
  const patchAt = (i) => (fragment) => setList(list.map((a, idx) => (idx === i ? { ...a, ...fragment } : a)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = list.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setList(next);
  };
  const remove = (i) => {
    setList(list.filter((_, idx) => idx !== i));
    if (openIdx === i) setOpenIdx(null);
  };
  const add = () => {
    const next = [...list, { id: `ta-${Math.random().toString(36).slice(2, 10)}`, name: "", slug: "", icon: "Sparkles", active: true }];
    setList(next);
    setOpenIdx(next.length - 1);
  };

  return (
    <div>
      <p className="text-sm font-medium text-ink mb-1">{field.label}</p>
      <p className="text-xs text-ink-muted mb-3">Click a row to edit the tile, the dedicated page, FAQs, and search-engine settings.</p>

      <div className="space-y-2">
        {list.map((area, i) => {
          const isOpen = openIdx === i;
          return (
            <div key={area.id || i} data-testid={`area-row-${i}`} className="rounded-lg border border-cream-2 bg-white overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3">
                <button
                  type="button"
                  data-testid={`area-toggle-${i}`}
                  onClick={() => setOpenIdx(isOpen ? null : i)}
                  className="flex-1 min-w-0 flex items-center gap-3 text-left"
                >
                  <span className="text-xs font-mono text-ink-muted w-6 shrink-0">#{i + 1}</span>
                  <span className="font-serif text-lg text-ink truncate">{area.name || <span className="text-ink-muted italic">Untitled area</span>}</span>
                  {area.active === false && (
                    <span className="text-xs text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">Hidden</span>
                  )}
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" data-testid={`area-up-${i}`} onClick={() => move(i, -1)} disabled={i === 0} title="Move up" className="p-1.5 rounded-md text-ink-soft hover:bg-cream-2 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <button type="button" data-testid={`area-down-${i}`} onClick={() => move(i, 1)} disabled={i === list.length - 1} title="Move down" className="p-1.5 rounded-md text-ink-soft hover:bg-cream-2 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  <button type="button" data-testid={`area-remove-${i}`} onClick={() => remove(i)} title="Delete" className="p-1.5 rounded-md text-red-600 hover:bg-red-50 transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    data-testid={`area-expand-${i}`}
                    onClick={() => setOpenIdx(isOpen ? null : i)}
                    className="ml-1 text-xs font-medium text-sage-dark hover:text-sage px-2 py-1"
                  >
                    {isOpen ? "Close" : "Edit"}
                  </button>
                </div>
              </div>
              {isOpen && <AreaFields area={area} patch={patchAt(i)} index={i} />}
            </div>
          );
        })}
      </div>

      <button type="button" data-testid="area-add" onClick={add} className="mt-4 inline-flex items-center gap-2 rounded-full bg-sage text-white px-5 py-2.5 text-sm font-semibold hover:bg-sage-dark transition-colors">
        <Plus className="h-4 w-4" /> Add treatment area
      </button>
    </div>
  );
};

export default TreatmentAreasField;
